import React, { useEffect, useState } from "react";

import { Autocomplete, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { CreateNewInvestorDrawer } from "components/deal/Investors/Create-new-investor-drawer/CreateNewInvestorDrawer";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook } from "hooks";
import { useDealActions } from "store";
import { IInvestor } from "store/types";

import { IInvestorsOptions } from "../AddNewLine/AddNewLine";

interface IData {
  id?: number;
  name?: string;
}

interface IProps {
  dealData: IData;
  roundData: IData;
  onSubmit: (result: { investorId: number; investorName: string }) => void;
  onCancel: () => void;
}

export const AddInvestor: React.FC<IProps> = ({ dealData, roundData, onSubmit, onCancel }) => {
  const [investor, setInvestor] = useState<IInvestorsOptions | null>(null);
  const { getAvailableInvestorsList, createDealRoundInvestors } = useDealActions();
  const { action: getInvestors, data } = useActionHook(getAvailableInvestorsList);
  const { action: createRoundInvestors } = useActionHook(createDealRoundInvestors);

  const fetchInvestors = () => {
    dealData.id && roundData.id && getInvestors({ deal_id: dealData.id, round_id: roundData.id });
  };

  useEffect(fetchInvestors, [dealData, roundData]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const saveNewInvestorToSelected = async (response: IInvestor) => {
    setInvestor({ id: response.id, name: response.name });
    fetchInvestors(); //refetch investors in case to add newly created investor to investors options for autocomplete
  };

  const onSave = () => {
    try {
      if (!dealData.id || !roundData.id || !investor?.id) return;

      createRoundInvestors(dealData.id, roundData.id, [investor.id]);

      onSubmit({
        investorId: investor.id,
        investorName: investor.name
      });
    } catch (err) {
      throw err;
    }
  };

  return (
    <>
      <Box
        minWidth="350px"
        maxWidth="350px"
        padding="25px 15px 25px 25px"
        borderRadius="8px"
        display="flex"
        flexDirection="column"
        gap="30px"
      >
        <Stack>
          <Typography marginBottom="10px" color="#0A3142">
            Investor
          </Typography>
          <MaterialTooltip title={dealData?.name} placement="top">
            <Typography textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap" color="#0A3142">
              {dealData?.name}
            </Typography>
          </MaterialTooltip>
          <Typography color="#5D7782">{roundData?.name}</Typography>
        </Stack>
        <Stack>
          <Autocomplete
            clearOnBlur={true}
            options={data || []}
            // getOptionDisabled={(option) => selectedInvestors.some((selected) => selected.id === option.id)}
            value={investor}
            onChange={(_, value) => {
              setInvestor(value);
            }}
            PaperComponent={({ children }) => {
              return (
                <Paper
                  sx={{
                    width: "305px",
                    backgroundColor: "white",
                    zIndex: "10",
                    borderRadius: "10px",
                    "& .MuiAutocomplete-listbox": {
                      "& .MuiAutocomplete-option[aria-disabled='true']": {
                        opacity: 1,
                        "&.Mui-focused": {
                          opacity: 1
                        }
                      },
                      "& .MuiAutocomplete-option[aria-selected='true']": {
                        bgcolor: "#edf1f9",
                        "&.Mui-focused": {
                          bgcolor: "#edf1f9"
                        }
                      }
                    },
                    "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                      bgcolor: "#edf1f9"
                    }
                  }}
                >
                  <>
                    {children}
                    <Button
                      onMouseDown={() => {
                        setIsDrawerOpen(true);
                      }}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        height: "50px",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                        borderRadius: "0 0 10px 10px"
                      }}
                      fullWidth
                    >
                      Add Investor
                    </Button>
                  </>
                </Paper>
              );
            }}
            getOptionLabel={(value) => {
              return value.name || "";
            }}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IInvestorsOptions) => (
              <li style={{ height: "43px", fontSize: "16px" }} {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => {
              return <TextField sx={{ width: "305px" }} {...params} label="Investor" />;
            }}
          />
        </Stack>
        <Stack display="flex" flexDirection="row" justifyContent="flex-end" gap="21px">
          <Button onClick={() => onCancel()} sx={{ maxWidth: "85px", minWidth: "85px" }} variant="outlined">
            Cancel
          </Button>
          <Button sx={{ maxWidth: "85px", minWidth: "85px" }} onClick={onSave} variant="contained">
            Save
          </Button>
        </Stack>
      </Box>
      <CreateNewInvestorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        saveNewInvestorToSelected={saveNewInvestorToSelected}
      />
    </>
  );
};
